import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ExternalLink } from "lucide-react";
import { EnvironmentVariables } from "./environment-variables";
import { ConfigurationFiles, UploadedFile } from "./configuration-files";
import { ProjectType } from "@/context/project";

interface Deployment {
  id: string;
  job: string;
  status: string;
  createdAt: string;
  jobId?: string;
  progress?: number;
}
interface DeploymentConfig {
  domain: string;
  region: string;
  size: string;
  environmentVars: { key: string; value: string }[];
  enableSSL: boolean;
  enableMonitoring: boolean;
  enableBackups: boolean;
}

const formatDeploymentDate = (dateString: string) => {
  const deploymentDate = new Date(dateString);
  const today = new Date();
  const isToday =
    deploymentDate.getDate() === today.getDate() &&
    deploymentDate.getMonth() === today.getMonth() &&
    deploymentDate.getFullYear() === today.getFullYear();

  if (isToday) {
    return deploymentDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  const isThisYear = deploymentDate.getFullYear() === today.getFullYear();
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
  };

  if (!isThisYear) {
    dateOptions.year = "numeric";
  }

  return `${deploymentDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })} on ${deploymentDate.toLocaleDateString("en-US", dateOptions)}`;
};

export function RedeploymentSettings({
  projectId,
  project,
  onRedeploy,
  isDeploying,
  deploymentFailed,
  deployMessage,
  deploymentProgress,
}: {
  projectId: string;
  project: ProjectType;
  onRedeploy: (
    type: string,
    options: {
      environmentVars: { key: string; value: string }[];
      files: UploadedFile[];
    },
  ) => Promise<void>;
  isDeploying: boolean;
  deployMessage: string;
  deploymentProgress: number;
  deploymentFailed: boolean;
}) {
  const [isDestroying, setIsDestroying] = React.useState(false);
  const [destroyMessage, setDestroyMessage] = React.useState("");
  const [config, setConfig] = useState<DeploymentConfig>({
    domain: project?.domainName || "",
    region: "",
    size: "",
    environmentVars: [],
    enableSSL: true,
    enableMonitoring: false,
    enableBackups: false,
  });
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  const prevIsDeploying = React.useRef(isDeploying);

  React.useEffect(() => {
    if (prevIsDeploying.current && !isDeploying && !deploymentFailed) {
      setConfig((prev) => ({ ...prev, environmentVars: [] }));
      setUploadedFiles([]);
    }
    prevIsDeploying.current = isDeploying;
  }, [isDeploying, deploymentFailed]);

  const handleDestroy = async () => {
    setIsDestroying(true);
    setDestroyMessage("Initiating destruction...");

    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_PUBLIC_API_ENDPOINT
        }/project/${projectId}/destroy`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = "Failed to destroy.";
        try {
          const errorResult = JSON.parse(errorText);
          errorMessage = errorResult.error?.message || errorMessage;
        } catch (e) {
          console.log("Error parsing error response JSON:", e);
          errorMessage = errorText.slice(0, 100) || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      setDestroyMessage(
        result.data?.message || "Destruction started successfully...",
      );
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unknown error occurred.";
      setDestroyMessage(`Error: ${errorMessage}`);
    } finally {
      setIsDestroying(false);
    }
  };

  useEffect(() => {
    if (project?.domainName) {
      updateConfig("domain", project.domainName);
    }
  }, [project?.domainName]);

  const updateConfig = (
    field: keyof DeploymentConfig,
    value: string | boolean | { key: string; value: string }[],
  ) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6 pt-2">
      <div className="space-y-4">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">
            IP Address
          </p>
          <p className="text-base font-semibold">
            {project?.ipAddress || "Not available"}
          </p>
        </div>

        <div>
          <h4 className="text-md mb-2 font-semibold">Application URLs</h4>
          <div className="space-y-2">
            {project?.apps?.map((app) => (
              <div
                key={app.id}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <a
                  href={`https://${app.name}.${project.domainName}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-500 hover:underline"
                >
                  <span className="font-medium">{`https://${app.name}.${project.domainName}`}</span>
                  <ExternalLink size={16} />
                </a>
                <Badge variant="secondary">{app.type}</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
      {["provisioned", "redeployed", "deployed"].includes(project?.status) && (
        <>
          <EnvironmentVariables
            ctaLabel="Add / Update Environment Variable"
            environmentVars={config.environmentVars}
            onEnvironmentVarsChange={(newVars) =>
              updateConfig("environmentVars", newVars)
            }
            disabled={isDeploying}
          />

          <ConfigurationFiles
            ctaLabel="Add / Update Files"
            uploadedFiles={uploadedFiles}
            onUploadedFilesChange={setUploadedFiles}
            disabled={isDeploying}
          />
          <div className="mt-6 flex justify-end gap-2">
            <Button
              onClick={() =>
                onRedeploy("redeploy", {
                  environmentVars: config.environmentVars,
                  files: uploadedFiles,
                })
              }
              disabled={isDeploying}
            >
              {isDeploying ? "Redeploying..." : "Redeploy"}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDestroy}
              disabled={isDestroying || isDeploying}
            >
              {isDestroying ? "Destroying..." : "Destroy"}
            </Button>
          </div>
          {/* {deployMessage && isDeploying && (
                  <p
                    className={`mt-2 text-sm ${
                      deployMessage.startsWith("Error:")
                        ? "text-red-500"
                        : "text-green-500"
                    }`}
                  >
                    {deployMessage}
                  </p>
                )} */}
          {destroyMessage && (
            <p
              className={`mt-2 text-sm ${
                destroyMessage.startsWith("Error:")
                  ? "text-red-500"
                  : "text-green-500"
              }`}
            >
              {destroyMessage}
            </p>
          )}
        </>
      )}

      <div className="mt-4 ml-8">
        <h3 className="text-md font-semibold">Deployment History</h3>
        <div className="relative mt-5">
          {project?.deployments?.length ? (
            <div className="relative">
              <div className="absolute top-9 bottom-9 left-[-2px] w-0.5 bg-slate-200" />
              {project?.deployments?.map(
                (deployment: Deployment, idx: number) => (
                  <div key={deployment.id} className="relative mb-8 ml-6">
                    <div className="absolute -left-[2.05rem] mt-9 h-4 w-4 rounded-none border-4 border-white bg-slate-400"></div>
                    <div className="rounded-lg bg-muted p-4 shadow-md">
                      <div className="flex items-center justify-between">
                        <h4 className="text-lg font-semibold">
                          {deployment.job}
                        </h4>
                        <Badge
                          className={
                            deployment.status === "success" ||
                            deployment.status === "completed"
                              ? "bg-green-800 text-white"
                              : deployment.status === "pending"
                                ? "bg-amber-600 text-white"
                                : deployment.status === "failed"
                                  ? "bg-red-800 text-white"
                                  : "bg-gray-500 text-white"
                          }
                        >
                          {deployment.status}
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {formatDeploymentDate(deployment.createdAt)}
                      </p>
                      {(deployment.status === "pending" ||
                        (deployment.status === "failed" &&
                          project.deployments?.[0].id === deployment.id)) && (
                        <div className="w-full pt-2">
                          <div className="mb-2 flex items-center gap-4">
                            <div className="align-start flex-1">
                              {deployMessage && (
                                <p
                                  className={`text-sm ${
                                    deployMessage.startsWith("Error:")
                                      ? "text-red-500"
                                      : "text-green-500"
                                  }`}
                                >
                                  {deployMessage}
                                </p>
                              )}
                            </div>
                            {deployment.status === "failed" &&
                              project.deployments?.[0].id === deployment.id &&
                              deploymentFailed && (
                                <Button
                                  onClick={() =>
                                    onRedeploy("redeploy", {
                                      environmentVars: config.environmentVars,
                                      files: uploadedFiles,
                                    })
                                  }
                                  disabled={isDeploying}
                                >
                                  Retry
                                </Button>
                              )}
                          </div>
                          {deployment.status === "pending" && isDeploying && (
                            <>
                              <Progress
                                value={deploymentProgress}
                                className="w-full"
                              />
                              <p className="text-right text-sm text-muted-foreground">
                                {deploymentProgress}%
                              </p>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ),
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No deployments found.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
